/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosCard, CosmosLink, CosmosText, EdsIcon } from "@components/elements";
import type { EdsIconType } from "@components/elements/atoms/eds-icon/EdsIcon.type";
import { css, cx } from "@panda/css";
import { Box, Flex } from "@panda/jsx";
import { Fragment } from "react";

/* -------------------------------------------------------------------------- *
 * Public types
 * -------------------------------------------------------------------------- */

/**
 * Logo treatment for the 80×80 circle on the left of the card.
 *
 * - `image` — full-bleed company logo (Training Partner).
 * - `icon`  — EDS icon illustration on a tinted backdrop. Tone follows `badge.tone`.
 * - omitted — no logo column, title + meta + tags shift left to align with
 *             the card padding (Gold Instructor without a profile photo).
 */
// Discriminant `kind` aligns with the rest of the Directory unions
// (`FilterDef.kind`, `SortOption.kind`, `SearchAccessor.type` is the
// remaining outlier — kept because `SearchAccessor` shipped before this
// convention took hold and renaming it is a downstream break).
export type DirectoryCardLogo = { kind: "image"; src: string; alt?: string } | { kind: "icon"; icon: EdsIconType };

/**
 * Visual tone of the badge pill + logo container.
 *
 * - `gold` — warm yellow→orange gradient. For "Gold instructor" / "Gold partner"
 *            variants where the gold gradient is brand-meaningful.
 * - `neutral` — cool transparent-white. For "Academic partner" and any non-gold
 *               variants where the warm gold would be misleading.
 *
 * Default: `gold`.
 */
export type DirectoryCardTone = "gold" | "neutral";

/**
 * Pill badge anchored to the top of the right column.
 *
 * Defaults render the Unreal mark to the left of the label. Pass `icon` to
 * swap the default mark for another EDS icon. `tone` controls both the badge
 * mini-mark AND the logo container styling (they stay in sync, per Figma).
 */
export type DirectoryCardBadge = {
	label: string;
	tone?: DirectoryCardTone;
	icon?: EdsIconType;
};

/**
 * Heading level for the card's name. Defaults to `h3` (cards usually live
 * under an `h2` section heading); pass a different level if the surrounding
 * page hierarchy requires it.
 */
export type DirectoryCardHeadingLevel = "h2" | "h3" | "h4";

/**
 * Single item in a meta row. Items render in a CSS grid so icons align
 * vertically across rows. When `href` is set the label renders as an
 * underlined link.
 */
export type DirectoryCardMetaItem = {
	icon?: EdsIconType;
	label: string;
	href?: string;
};

interface DirectoryCardBaseProps {
	name: string;

	/** Pill rendered above the name. */
	badge?: DirectoryCardBadge;

	/** Optional 80×80 logo / icon-illustration on the left of the card. */
	logo?: DirectoryCardLogo;

	/**
	 * First meta row — typically underlined links such as Website + Email.
	 * Empty/omitted hides the row.
	 */
	links?: DirectoryCardMetaItem[];

	/**
	 * Second meta row — typically plain text such as Country + Language.
	 * Empty/omitted hides the row.
	 */
	meta?: DirectoryCardMetaItem[];

	/** Primary tags ("Tag Link" in Figma — filled background). */
	primaryTags?: string[];

	/** Secondary tags ("Tag Static" in Figma — outlined). */
	secondaryTags?: string[];

	/**
	 * Heading element rendered for the name. Defaults to `h3`.
	 */
	headingLevel?: DirectoryCardHeadingLevel;

	/**
	 * Class applied to the outer `CosmosCard` surface. The card also exposes a
	 * stable external selector hook of `.directory-card`.
	 */
	className?: string;
}

/**
 * `href` + `hrefLabel` form a discriminated chevron contract: setting one
 * requires the other. The chevron link's accessible name MUST announce the
 * destination ("View Acme partner profile") — falling back to the card's
 * name alone leaves AT users without verb / destination context. Reserved
 * for the future Service Partner clickable variant — Gold Instructor /
 * Training Partner adapters omit both fields.
 *
 * Absolute URLs (`http(s)://…`) automatically open in a new tab with
 * `rel="noopener noreferrer"`; same-origin paths follow CosmosLink defaults.
 */
export type DirectoryCardProps = DirectoryCardBaseProps &
	({ href: string; hrefLabel: string } | { href?: never; hrefLabel?: never });

/* -------------------------------------------------------------------------- *
 * Tone-driven style fragments
 * -------------------------------------------------------------------------- */

// Soft gold gradient backdrop for the logo circle (10% alpha per Figma).
const goldLogoBg = "linear-gradient(225deg, rgba(251, 237, 156, 0.1) 0%, rgba(254, 133, 57, 0.1) 100%)";

// Full-strength gold gradient for the badge mini-mark (100% alpha per Figma).
const goldMarkBg = "linear-gradient(225deg, rgba(251, 237, 156, 1) 0%, rgba(254, 133, 57, 1) 100%)";

/* -------------------------------------------------------------------------- *
 * Card surface + content
 * -------------------------------------------------------------------------- */

const cardStyles = css({
	minW: "0",
	height: "full",
	// Hover styling intentionally NOT applied to the entire card surface — only
	// the chevron link in the top-right is interactive when `href` is set, so
	// surfacing a card-wide hover would misrepresent the click target.
});

const contentStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.20",
	minWidth: "0",
	width: "full",
});

/* ---------- top row: optional logo + (badge + name) + optional chevron ---- */

const topRowStyles = css({
	display: "flex",
	gap: "eds.dimension.20",
	alignItems: "center",
	width: "full",
});

/**
 * Logo container — exact Figma measurements from node 76:8856:
 *   - 88×88 outer frame (`logoContainerStyles`)
 *   - 80×80 inner backdrop circle inset 4px (`logoInnerBase`)
 *   - 38×38 icon centered inside the 80×80 (so 21px gap from icon to ring)
 *   - Ring rendered via inline SVG `<circle stroke="url(#…)" />` — vector
 *     rendering avoids the blocky `mask-composite` artifacts that plagued
 *     earlier CSS-only attempts.
 *
 * For image logos (partners), `padding: 12px` inside the 80×80 inner
 * leaves a small visible gap from the ring while letting the logo
 * dominate. Icon variant uses no padding — the icon is intrinsically
 * smaller (38 vs 80) so the gap is from `justify/align center`.
 */
const logoContainerStyles = css({
	position: "relative",
	flexShrink: 0,
	width: "88px",
	height: "88px",
});

const logoInnerBase = css({
	position: "absolute",
	inset: "4px",
	width: "80px",
	height: "80px",
	borderRadius: "50%",
	overflow: "hidden",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	"& svg": {
		width: "38px",
		height: "38px",
	},
	"& img": {
		width: "full",
		height: "full",
		objectFit: "contain",
		display: "block",
		// Padding inside the backdrop so partner logos don't touch the ring.
		// Icons (38×38) already have 21px breathing room via center-alignment;
		// images need explicit padding to achieve a similar gap.
		padding: "eds.dimension.12",
	},
});

const logoRingSvgStyles = css({
	position: "absolute",
	inset: 0,
	width: "88px",
	height: "88px",
	pointerEvents: "none",
});

// Gold tone — warm yellow→orange backdrop + gradient stroke ring.
const logoInnerGold = css({
	color: "eds.palette.orange.400",
});

// Neutral tone — transparent-white backdrop + subtle solid stroke ring.
const logoInnerNeutral = css({
	backgroundColor: "eds.palette.transparent.light.015",
	color: "eds.text.primary",
	backdropFilter: "blur(37.5px)",
});

const titleColumnStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.10",
	flex: "1 1 auto",
	minWidth: "0",
	alignSelf: "center",
});

const nameStyles = css({
	color: "eds.text.primary",
	textWrap: "balance",
});

/* ---------- badge pill ---------------------------------------------------- */

const badgePillStyles = css({
	display: "inline-flex",
	alignItems: "center",
	gap: "eds.dimension.6",
	paddingInlineStart: "eds.dimension.6",
	paddingInlineEnd: "eds.dimension.8",
	paddingBlock: "eds.dimension.6",
	borderRadius: "eds.borderRadius.6",
	backgroundColor: "eds.palette.transparent.light.005",
	borderWidth: "1px",
	borderStyle: "solid",
	borderColor: "eds.palette.transparent.light.010",
	maxWidth: "fit-content",
});

// Exact Figma 76:8862 spec: 16×16 box, 4.571px radius, 0.286px subtle white
// border, gradient backdrop. Icon (76:8863) at 9.429×9.429 — needs `!important`
// to win over EdsIcon's inline `size="xs"` width/height attributes (the
// wrapper's prop sets the SVG width attribute directly, which CSS without
// `!important` can't override).
const badgeMarkBase = css({
	width: "16px",
	height: "16px",
	borderRadius: "4.571px",
	overflow: "hidden",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: 0,
	borderWidth: "0.286px",
	borderStyle: "solid",
	borderColor: "rgba(255, 255, 255, 0.15)",
	"& svg": {
		width: "9.429px !important",
		height: "9.429px !important",
	},
});

const badgeMarkGold = css({
	background: goldMarkBg,
	color: "#1A1106",
	"& svg": { color: "#1A1106" },
});

const badgeMarkNeutral = css({
	backgroundColor: "eds.palette.transparent.light.015",
	color: "eds.text.primary",
});

/* ---------- meta rows — shared-column grid for vertical icon alignment ----- */

/**
 * Meta section renders as a single CSS grid whose columns are auto-sized
 * across ALL rows. Each row places its items at the same column positions
 * (item, dot, item, dot, item, …), so icons in column 1 of row 1 vertically
 * align with icons in column 1 of row 2, etc. — matching the Figma layout.
 *
 * The grid template width is `auto auto auto … auto` with `2 × maxItems - 1`
 * columns, where `maxItems` is the max item count across the rows passed in.
 * Variable row lengths (2 links + 1 meta, or 3 links + 2 meta) are handled
 * by rendering empty placeholder cells for shorter rows — column widths stay
 * stable across the whole meta block.
 */
const metaGridBaseStyles = css({
	display: "grid",
	columnGap: "eds.dimension.6",
	rowGap: "eds.dimension.4",
	alignItems: "center",
	justifyContent: "start",
	width: "full",
});

const metaItemStyles = css({
	display: "inline-flex",
	alignItems: "center",
	gap: "eds.dimension.6",
	color: "eds.text.primary",
	"& svg": {
		flexShrink: 0,
	},
});

const metaLinkStyles = css({
	textDecoration: "underline",
	textDecorationSkipInk: "none",
	color: "eds.text.primary",
	transition: "opacity 160ms ease-out",
	"&:hover, &:focus-visible": {
		color: "eds.text.primary",
		opacity: 0.85,
	},
});

const metaDotStyles = css({
	display: "inline-block",
	minWidth: "4px",
	textAlign: "center",
	color: "eds.text.secondary",
});

/* ---------- tag rows ------------------------------------------------------ */

const tagsSectionStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.8",
	width: "full",
});

const tagsRowStyles = css({
	display: "flex",
	flexWrap: "wrap",
	gap: "eds.dimension.8",
});

const tagPillBase = css({
	display: "inline-flex",
	alignItems: "center",
	height: "24px",
	paddingInline: "eds.dimension.10",
	borderRadius: "9999px",
	whiteSpace: "nowrap",
	color: "eds.text.primary",
	// Matches Figma material/blur/75 on Tag Link / Tag Static.
	backdropFilter: "blur(37.5px)",
});

const tagPillFilled = css({
	backgroundColor: "eds.palette.transparent.light.015",
});

const tagPillOutlined = css({
	backgroundColor: "transparent",
	borderWidth: "1px",
	borderStyle: "solid",
	borderColor: "eds.palette.transparent.light.035",
});

/* ---------- chevron link (Service Partner — clickable variant) ----------- */

const chevronButtonStyles = css({
	flexShrink: 0,
	width: "40px",
	height: "40px",
	borderRadius: "9999px",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	backgroundColor: "eds.palette.transparent.light.015",
	color: "eds.text.primary",
	transition: "background-color 160ms ease-out, transform 160ms ease-out",
	"&:hover": {
		backgroundColor: "eds.palette.transparent.light.035",
		color: "eds.text.primary",
	},
	"&:focus-visible": {
		backgroundColor: "eds.palette.transparent.light.035",
		color: "eds.text.primary",
		outlineWidth: "2px",
		outlineStyle: "solid",
		// Solid color — `transparent.light.075` (~7.5% white) failed WCAG 3:1
		// against the blurred card surface; `eds.text.primary` is full-alpha
		// and audited for AA contrast on every Cosmos background.
		outlineColor: "eds.text.primary",
		outlineOffset: "2px",
	},
	"@media (prefers-reduced-motion: no-preference)": {
		"&:hover": {
			transform: "translateX(2px)",
		},
	},
	"& svg": {
		width: "20px",
		height: "20px",
	},
});

/* -------------------------------------------------------------------------- *
 * Sub-components
 * -------------------------------------------------------------------------- */

function DirectoryCardLogoEl({ logo, tone }: { logo: DirectoryCardLogo; tone: DirectoryCardTone }): React.JSX.Element {
	const innerClass = tone === "gold" ? logoInnerGold : logoInnerNeutral;
	const goldBackground = tone === "gold" ? { background: goldLogoBg } : undefined;

	return (
		<div className={logoContainerStyles}>
			<div className={cx(logoInnerBase, innerClass)} style={goldBackground}>
				{logo.kind === "image" ? (
					<img src={logo.src} alt={logo.alt ?? ""} width={80} height={80} loading="lazy" decoding="async" />
				) : (
					<EdsIcon icon={logo.icon} aria-hidden="true" />
				)}
			</div>
			{/*
				Ring rendered as SVG `<circle stroke=…>` matching Figma node 76:8859
				(88×88 outer ellipse). Vector stroke is crisp at any DPR; supports
				the linear gradient natively.
			*/}
			<svg className={logoRingSvgStyles} viewBox="0 0 88 88" aria-hidden="true">
				{tone === "gold" ? (
					<defs>
						<linearGradient id="cosmosDirectoryCardRingGold" x1="100%" y1="0%" x2="0%" y2="100%">
							<stop offset="0%" stopColor="rgb(251, 237, 156)" />
							<stop offset="100%" stopColor="rgb(254, 133, 57)" />
						</linearGradient>
					</defs>
				) : null}
				<circle
					cx="44"
					cy="44"
					r="43"
					fill="none"
					stroke={tone === "gold" ? "url(#cosmosDirectoryCardRingGold)" : "rgba(255, 255, 255, 0.15)"}
					strokeWidth="2"
				/>
			</svg>
		</div>
	);
}

function DirectoryCardBadgeEl({ badge }: { badge: DirectoryCardBadge }): React.JSX.Element {
	const tone = badge.tone ?? "gold";
	const markClass = tone === "gold" ? badgeMarkGold : badgeMarkNeutral;
	const markIcon: EdsIconType = badge.icon ?? "UnrealEngine";

	return (
		<span className={badgePillStyles}>
			<span className={cx(badgeMarkBase, markClass)} aria-hidden="true">
				<EdsIcon icon={markIcon} size="xs" aria-hidden="true" />
			</span>
			<CosmosText variant="uiSM" color="eds.text.primary">
				{badge.label}
			</CosmosText>
		</span>
	);
}

const isAbsoluteUrl = (href: string): boolean => /^https?:\/\//i.test(href);

function MetaItemEl({ item }: { item: DirectoryCardMetaItem }): React.JSX.Element {
	const label = (
		<CosmosText variant="uiMD" color="eds.text.primary">
			{item.label}
		</CosmosText>
	);
	const external = item.href ? isAbsoluteUrl(item.href) : false;
	return (
		<span className={metaItemStyles}>
			{item.icon ? <EdsIcon icon={item.icon} size="sm" aria-hidden="true" /> : null}
			{item.href ? (
				<CosmosLink
					href={item.href}
					className={metaLinkStyles}
					{...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
				>
					{label}
				</CosmosLink>
			) : (
				label
			)}
		</span>
	);
}

/**
 * Renders 1+ rows of meta items into a single CSS grid. Columns are
 * auto-sized across all rows, so icons in column 1 of row 1 vertically
 * align with icons in column 1 of row 2 (Figma intent).
 *
 * Layout per row: [item, dot, item, dot, item, …] — items and dots occupy
 * the same column slots in every row. Rows with fewer items than the max
 * render empty cells in the trailing slots so column widths stay stable.
 */
function DirectoryCardMetaGrid({ rows }: { rows: DirectoryCardMetaItem[][] }): React.JSX.Element {
	const maxItems = Math.max(1, ...rows.map((r) => r.length));
	const cellCount = maxItems * 2 - 1;
	const gridTemplateColumns = Array(cellCount).fill("auto").join(" ");

	return (
		<div className={metaGridBaseStyles} style={{ gridTemplateColumns }}>
			{rows.map((row, rowIdx) => {
				const cells: React.ReactNode[] = [];
				for (let i = 0; i < cellCount; i++) {
					const isItemCell = i % 2 === 0;
					const itemIdx = i / 2;
					if (isItemCell) {
						const item = row[itemIdx];
						cells.push(
							item ? (
								<MetaItemEl key={`r${rowIdx}-c${i}`} item={item} />
							) : (
								<span key={`r${rowIdx}-c${i}`} aria-hidden="true" />
							),
						);
					} else {
						// dot cell — render `·` only if items exist on both sides in this row
						const prevItemIdx = (i - 1) / 2;
						const nextItemIdx = (i + 1) / 2;
						const showDot = Boolean(row[prevItemIdx]) && Boolean(row[nextItemIdx]);
						cells.push(
							showDot ? (
								<span key={`r${rowIdx}-c${i}`} className={metaDotStyles} aria-hidden="true">
									·
								</span>
							) : (
								<span key={`r${rowIdx}-c${i}`} aria-hidden="true" />
							),
						);
					}
				}
				return <Fragment key={`row-${rowIdx}`}>{cells}</Fragment>;
			})}
		</div>
	);
}

function DirectoryCardTagRowEl({
	tags,
	variant,
}: {
	tags: string[];
	variant: "filled" | "outlined";
}): React.JSX.Element {
	const pillClass = variant === "filled" ? tagPillFilled : tagPillOutlined;
	return (
		<div className={tagsRowStyles}>
			{tags.map((tag) => (
				<span key={tag} className={cx(tagPillBase, pillClass)}>
					<CosmosText variant="uiSM" color="eds.text.primary">
						{tag}
					</CosmosText>
				</span>
			))}
		</div>
	);
}

/* -------------------------------------------------------------------------- *
 * DirectoryCard
 * -------------------------------------------------------------------------- */

export function DirectoryCard({
	name,
	badge,
	logo,
	links = [],
	meta = [],
	primaryTags = [],
	secondaryTags = [],
	headingLevel = "h3",
	href,
	hrefLabel,
	className,
}: DirectoryCardProps): React.JSX.Element {
	const clickable = Boolean(href);
	const tone = badge?.tone ?? "gold";
	const chevronExternal = href ? isAbsoluteUrl(href) : false;

	const metaRows: DirectoryCardMetaItem[][] = [];
	if (links.length > 0) metaRows.push(links);
	if (meta.length > 0) metaRows.push(meta);

	const hasPrimary = primaryTags.length > 0;
	const hasSecondary = secondaryTags.length > 0;
	const hasTagsSection = hasPrimary || hasSecondary;

	return (
		<CosmosCard
			className={cx("directory-card", cardStyles, className)}
			variant="material"
			borderRadius={24}
			data-clickable={clickable ? "true" : "false"}
		>
			<CosmosCard.Content>
				<Box className={contentStyles}>
					{/* Top row: optional logo + (badge + name column) + optional chevron link */}
					<Flex className={topRowStyles}>
						{logo ? <DirectoryCardLogoEl logo={logo} tone={tone} /> : null}
						<Box className={titleColumnStyles}>
							{badge ? <DirectoryCardBadgeEl badge={badge} /> : null}
							<CosmosText variant="headingMD" as={headingLevel} className={nameStyles}>
								{name}
							</CosmosText>
						</Box>
						{href ? (
							<CosmosLink
								href={href}
								className={chevronButtonStyles}
								aria-label={hrefLabel ?? name}
								{...(chevronExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
							>
								<EdsIcon icon="ChevronRight" aria-hidden="true" />
							</CosmosLink>
						) : null}
					</Flex>

					{/* Meta grid — links row above plain-meta row, icons align across columns */}
					{metaRows.length > 0 ? <DirectoryCardMetaGrid rows={metaRows} /> : null}

					{/* Tag rows: primary (filled) + optional secondary (outlined). */}
					{hasTagsSection ? (
						<Box className={tagsSectionStyles}>
							{hasPrimary ? <DirectoryCardTagRowEl tags={primaryTags} variant="filled" /> : null}
							{hasSecondary ? <DirectoryCardTagRowEl tags={secondaryTags} variant="outlined" /> : null}
						</Box>
					) : null}
				</Box>
			</CosmosCard.Content>
		</CosmosCard>
	);
}
